import { flushSync } from 'react-dom';
import cloneDeep from 'lodash/cloneDeep';
import type {
    AspectRatio,
    AudioSourceData,
    MediaSourceData,
    OpaqueVideoData,
    SceneGroup,
    TemplateBundle,
} from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';

import type {
    AssetTranscript,
    AudioAssetModel,
    GenerateRecommendedVideoResponse,
    GenerateScriptResponse,
    MediaOptionsRequest,
    SceneRecommendation,
    SlideBackgroundModel,
    VideoPlan,
} from 'js/src/client';
import {
    ApiService,
    CreationSourceEnum,
    DurationTargetDefaults,
    LanguageEnum,
    NarrativeThreadEnum,
    SlideBackgroundModelTypeEnum,
    TaskResultMessageTypeEnum,
} from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { SKIP_SCRIPT_COMPOSER_PARAM } from 'js/src/components/dashboard/utils/dashboardSketchStorage';
import {
    avatarBackgroundVideoAsset,
    avatarVideoAsset,
    ensureAIAvatarsLoaded,
    getAIAvatarById,
    getAIAvatarVoicePresetByAvatarId,
} from 'js/src/data/ai-voices-and-avatars';
import {
    getScriptComposerPlaceholderVideoId,
    getVideoDataHash,
    getVideoId,
    getVideoName,
    setSceneHandoffActive,
    setScriptComposerPlaceholderVideoId,
    setSharePageUrl,
    setToolboxCreatingVideo,
    setVideoDataHash,
    setVideoIdAndHookupAnalytics,
    setVideoLanguage,
    VIDEO_INIT_TYPE,
} from 'js/src/data/app';
import { fetchBlueprints, getOrFetchBlueprint } from 'js/src/data/blueprints';
import { setLuminaryTemplate } from 'js/src/data/clip-creator/clip-creator-utils';
import type { ImmutableAudioAsset } from 'js/src/data/media';
import {
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
    isImmutableAudioAsset,
} from 'js/src/data/media/media-types';
import { selectMusicRecommendationMode } from 'js/src/data/media-recommendation-preferences';
import { clearAllAgentActivity } from 'js/src/data/agent-activity';
import { getCurrentAIAvatarVoicePreset } from 'js/src/data/script-composer/script-composer-selectors';
import { selectSketchControlsBrandKitId } from 'js/src/data/sketch-controls/sketch-controls-selectors';
import {
    getTemplateBundle,
    getTemplateDefaultSceneDesign,
    getTemplateDefaultStoryboardSceneDesign,
    loadTemplateAndSceneDesigns,
    storeTemplate,
} from 'js/src/data/templates';
import { getTranscriptsToSave } from 'js/src/data/transcript';
import { getLanguageTextDirection, loadSupportedLanguages } from 'js/src/data/translations';
import { resolveUploadedAssetFromRegistries } from 'js/src/agent/registries/resolveUploadedAsset';
import { getUploadedAssetByUuid } from 'js/src/data/uploads/uploads-selectors';
import {
    addVideoDraft,
    setVideoDraftError,
    setVideoDraftGenerating,
    setVideoDraftPreviewCanvas,
    setVideoDraftSavedVideoId,
} from 'js/src/data/video-planner/video-planner-actions';
import {
    getVideoDraftById,
    selectSelectedVideoDraft,
    selectVideoDrafts,
} from 'js/src/data/video-planner/video-planner-selectors';
import type { VideoDraftCreationTool } from 'js/src/data/video-planner/video-planner-types';
import {
    createRecommendationAIVoiceover,
    getVideoDraftLuminaryResolution,
} from 'js/src/data/video-planner/video-planner-utils';
import { handleVideoLimitError } from 'js/src/hooks/video-limit-error';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import QueryStringManipulator from 'js/src/libs/querystring-manipulator';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import { TaskQueue } from 'js/src/libs/task-queue';
import { parseScript } from 'js/src/libs/transcript';
import { DEFAULT_LUMINARY_CONFIG } from 'js/src/libs/video-data-consts';
import WebSocketClient from 'js/src/libs/websocket-client';
import {
    addSceneGroupFromVoiceoverAssetOperation,
    addScenesFromRecommendations,
    applyTalkingHeadMediaOperation,
    applyTemplateStyleFromVideoData,
    createBestGuessTranscript,
    createNewVoicedrivenSceneGroupOperation,
    fetchAndSetRecommendedSceneAttributesOperation,
    getNarrativeThreads,
    setAndApplyVoiceoverAssetForSceneGroupOperation,
    setCapabilitiesOperation,
} from 'js/src/luminary';
import { loadAndApplyBrandKitToLuminary } from 'js/src/luminary/brand-kit-operations';
import { awaitCanPlayWithTimeout } from 'js/src/luminary/can-play-utils';
import { createCall, makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { reportLegacyVideoRecommendationCallForChatUser } from 'js/src/luminary/luminary-operations/core';
import {
    isTalkingHeadSceneGroup,
    isVoiceDrivenSceneGroup,
} from 'js/src/luminary/luminary-selectors';
import { isErrorObject } from 'js/src/types';

type VideoPlanCreationData = {
    videoPlan: VideoPlan;
    referenceMaterial: string;
    templateStylingVideoData: OpaqueVideoData | null;
    sceneRecommendations: SceneRecommendation[];
    voiceoverAsset: AudioAssetModel | null;
    voiceoverTranscript: AssetTranscript | null;
    musicAsset: AudioAssetModel | null;
    brandKitId: number | null;
};

/**
 * Applies a template bundle and its capabilities to a luminary. Shared by
 * every code path that turns a template into an editable luminary (blank-video
 * creation and plan-driven creation), so the template setup stays identical
 * across them. Callers set the aspect ratio afterwards (the ordering relative
 * to other styling steps differs per caller).
 */
function applyTemplateAndCapabilities(
    luminary: Luminary,
    templateBundle: TemplateBundle,
    dispatch: AppDispatch,
    getState: GetRootState,
): void {
    luminary.setTemplate(templateBundle);

    const { setCapabilities } = makeOperationsCallable(
        { setCapabilitiesOperation },
        luminary,
        dispatch,
        getState,
    );
    setCapabilities();
}

export async function loadVideoPlanIntoLuminary(
    luminary: Luminary,
    creationData: VideoPlanCreationData,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<void> {
    const {
        videoPlan,
        templateStylingVideoData,
        sceneRecommendations,
        voiceoverAsset,
        voiceoverTranscript,
        musicAsset,
        brandKitId,
    } = creationData;

    const defaultSceneDesign = getTemplateDefaultSceneDesign(getState(), videoPlan.template_id);
    if (!defaultSceneDesign) {
        throw new Error('Default scene design not found');
    }
    const templateBundle = getTemplateBundle(getState(), videoPlan.template_id);
    if (!templateBundle) {
        throw new Error('Template bundle not found');
    }

    await luminary.clear();

    const loadedSceneRecommendations = sceneRecommendations.filter(
        (recommendation) => recommendation !== null,
    );

    applyTemplateAndCapabilities(luminary, templateBundle, dispatch, getState);

    if (templateStylingVideoData) {
        await applyTemplateStyleFromVideoData(luminary, cloneDeep(templateStylingVideoData));
    }

    luminary.aspectRatio = videoPlan.aspect_ratio as unknown as AspectRatio;

    const recommendationLanguage = loadedSceneRecommendations
        .map((rec) => rec.scene_data?.language as LanguageEnum | undefined)
        .find((lang) => lang && lang !== LanguageEnum.XX);
    if (recommendationLanguage) {
        await dispatch(loadSupportedLanguages());
        luminary.textDirection = getLanguageTextDirection(getState(), recommendationLanguage);
    }

    if (videoPlan.narrative_thread === NarrativeThreadEnum.TEXT) {
        const call = createCall({ luminary, dispatch, getState });
        addScenesFromRecommendations(
            loadedSceneRecommendations,
            luminary,
            dispatch,
            {},
            defaultSceneDesign,
            'video_plan',
            call,
            false,
            false, // isGlobalVideo
            true,
            '',
        );
    } else if (
        videoPlan.narrative_thread === NarrativeThreadEnum.TALKING_HEAD ||
        videoPlan.narrative_thread === NarrativeThreadEnum.VOICEOVER
    ) {
        let avatarAsset: ImmutableMap<SlideBackgroundModel> | null = null;
        let avatarVoicePreset: ReturnType<typeof getCurrentAIAvatarVoicePreset> = null;
        if (videoPlan.narrative_thread === NarrativeThreadEnum.TALKING_HEAD) {
            if (!videoPlan.ai_avatar) {
                throw new Error('No AI avatar selected for talking head video plan');
            }
            await dispatch(ensureAIAvatarsLoaded());
            const avatar = getAIAvatarById(getState(), videoPlan.ai_avatar);
            if (!avatar) {
                throw new Error('AI avatar not found');
            }
            avatarAsset = avatarVideoAsset(avatar);
            if (!avatarAsset) throw new Error('Avatar asset not found');
            // Scope the background to this plan's avatar. getCurrentAIAvatarVoicePreset
            // falls back to preset index 0 when script_composer has no selection (the
            // sketch path never sets one), which would pull an unrelated avatar's
            // background now that the preset ORM is populated here.
            avatarVoicePreset =
                getAIAvatarVoicePresetByAvatarId(getState(), avatar.get('id')) ??
                getCurrentAIAvatarVoicePreset(getState());
        }
        const avatarBackgroundAssetData = avatarAsset
            ? avatarBackgroundVideoAsset(avatarVoicePreset)
            : null;
        if (!videoPlan.ai_voice) {
            throw new Error('No AI voice selected for voice-driven video plan');
        }

        let audioAsset: ImmutableAudioAsset;
        let transcript: AssetTranscript | null = voiceoverTranscript || null;
        if (voiceoverAsset) {
            if (!voiceoverAsset) throw new Error('Video plan voiceover asset data not found');
            audioAsset = ImmutableFromJS(voiceoverAsset);
        } else {
            const bestGuessTranscript = createBestGuessTranscript(loadedSceneRecommendations);
            audioAsset = bestGuessTranscript.audioAsset;
            transcript = bestGuessTranscript.transcript;
        }

        const { addSceneGroupFromVoiceoverAsset } = makeOperationsCallable(
            { addSceneGroupFromVoiceoverAssetOperation },
            luminary,
            dispatch,
            getState,
        );

        await addSceneGroupFromVoiceoverAsset({
            targetIndex: 0,
            audioAssetData: audioAsset,
            avatarAssetData: avatarAsset,
            avatarBackgroundAssetData,
            deleteOtherScenes: true,
            analyticsMetadata: { method: 'AI Voice Script Composer Blueprint' },
            selectedBlueprintId: videoPlan.blueprint_id,
            generateRecommendedVideoResponse: null,
            sceneRecommendations: loadedSceneRecommendations,
            isGlobalVideo: false,
            transcript,
            systemTriggered: true,
            parentActionGroup: 'video-planner',
        });
    } else {
        throw new Error('Invalid narrative thread');
    }

    if (musicAsset) {
        try {
            luminary.music = musicAsset as AudioSourceData;
        } catch (error) {
            ErrorReporter.capture(error, {
                extra: { videoPlan, musicAsset },
            });
        }
    }

    if (brandKitId) {
        await dispatch(loadAndApplyBrandKitToLuminary(luminary, brandKitId));
    }
}

export function saveVideoDraftToDatabaseOperation(videoDraftId: string) {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<number> => {
        return saveVideoDraftToDatabase(videoDraftId, dispatch, getState);
    };
}

function hashVideoData(videoData: string): string {
    let hash = 0;
    for (let i = 0; i < videoData.length; i++) {
        const char = videoData.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0;
    }
    return hash.toString(36);
}

// Create a brand-new DB video directly from a luminary (no draft), seed the
// editor's server data hash so subsequent autosaves work, and return the new
// videoId. Used by the single-video sketch-in-creator flow, where the agent
// builds on the editor luminary itself and there is no separate draft to adopt.
// Persist the luminary's current state into an existing DB video and seed the
// redux state that autosave/publishing need afterwards.
async function persistLuminaryIntoVideo({
    luminary,
    videoId,
    videoDataHash,
    brandKitId,
    dispatch,
    getState,
}: {
    luminary: Luminary;
    videoId: number;
    videoDataHash: string;
    brandKitId?: number | null;
    dispatch: AppDispatch;
    getState: GetRootState;
}): Promise<void> {
    const updateResponse = await ApiService.videoUpdate({
        path: { video_id: videoId.toString() },
        body: {
            video_data: luminary.save(),
            script: parseScript(luminary),
            blueprint_id: null,
            transcripts: getTranscriptsToSave(getState()),
            narrative_threads: getNarrativeThreads(luminary),
            duration_seconds: Math.floor(luminary.duration / 1000),
            user_overrides: {},
            user_uploads: [],
            brand_kit_id: brandKitId ?? null,
            template_id: luminary.template.id,
            aspect_ratio: luminary.aspectRatio,
            video_data_hash: videoDataHash,
            video_updated_by_user: false,
            video_init_type: VIDEO_INIT_TYPE.CREATE_FROM_SCRATCH,
            script_composer_data: null,
            article_original_content: null,
            tab_durations: null,
            render: false,
            async_generation_id: null,
            async_generation_data: null,
        },
    });

    // Autosave saves against the server's current data hash; seed it now so
    // the very next autosave of this video succeeds.
    dispatch(setVideoDataHash({ videoDataHash: updateResponse.data.videoDataHash }));

    // Seed the share page URL so publishing can navigate to it. Unlike the
    // normal create flow, this path never re-loads the video through
    // hydrateVideoCreatorReduxStoresAction, so without this the URL stays
    // null and publishing fails with "Share page URL is undefined".
    dispatch(setSharePageUrl({ sharePageUrl: updateResponse.data.share_page_url }));

    // Seed the video language. Same rationale as the share page URL above:
    // without it the value stays null and getVideoLanguageOrThrow throws —
    // e.g. media search click-logging fails the moment the agent searches
    // media.
    if (updateResponse.data.language) {
        dispatch(setVideoLanguage({ video_language: updateResponse.data.language }));
    }
}

export function createVideoFromLuminaryOperation({
    luminary,
    name,
    brandKitId,
    onVideoCreated,
}: {
    luminary: Luminary;
    name: string;
    brandKitId?: number | null;
    // Fires once the video exists in the database, so the caller can adopt it
    // even if a later step fails rather than orphaning it.
    onVideoCreated?: (videoId: number) => void;
}) {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<number> => {
        if (luminary.sceneGroups.length === 0) {
            throw new Error('Cannot create a video from an empty luminary (no scene groups).');
        }

        // Wait for canPlay so template.id stabilizes before we read it below.
        // Bounded: a lost WebGL context leaves onCanPlay() pending forever, which
        // would wedge the turn-end handoff and strand the user on the generating
        // preview with no error. The timeout turns that into a reported rejection.
        await awaitCanPlayWithTimeout(luminary);

        const response = await ApiService.videoCreate({
            body: {
                video_data: luminary.save(),
                aspect_ratio: luminary.aspectRatio,
                template: luminary.template.id,
                script_composer_data: null,
                name,
                creation_source: CreationSourceEnum.SKETCH,
                script: parseScript(luminary),
                transcripts: getTranscriptsToSave(getState()),
                narrative_threads: getNarrativeThreads(luminary),
                duration_seconds: Math.floor(luminary.duration / 1000),
                brand_kit_id: brandKitId ?? null,
            },
        });
        const video_id = response.data.video_id;
        onVideoCreated?.(video_id);

        // Autosave saves against the server's current data hash; seed it now so
        // the very next autosave of this video succeeds.
        dispatch(setVideoDataHash({ videoDataHash: response.data.video_data_hash }));

        // This path never re-loads the video through
        // hydrateVideoCreatorReduxStoresAction, so nothing else seeds these:
        // publishing needs the share page URL, and getVideoLanguageOrThrow-backed
        // paths (e.g. media search click-logging) need the language.
        dispatch(setSharePageUrl({ sharePageUrl: response.data.share_page_url }));
        if (response.data.language) {
            dispatch(setVideoLanguage({ video_language: response.data.language }));
        }

        return video_id;
    };
}

// Adopts the placeholder video the script-composer hand-off parked: saves the
// built luminary into that existing DB video (creating a new one would orphan
// it) and renames it. Persisting also nulls script_composer_data, so a refresh
// reopens the finished video rather than the composer.
export function adoptScriptComposerPlaceholderVideoOperation({
    luminary,
    videoId,
    name,
    brandKitId,
}: {
    luminary: Luminary;
    videoId: number;
    name: string;
    brandKitId?: number | null;
}) {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<void> => {
        if (luminary.sceneGroups.length === 0) {
            throw new Error('Cannot adopt the placeholder video from an empty luminary.');
        }

        // Wait for canPlay so template.id stabilizes before we read it below.
        // Bounded so a lost WebGL context can't leave the handoff hanging (see
        // createVideoFromLuminaryOperation).
        await awaitCanPlayWithTimeout(luminary);

        // The hash was seeded when the placeholder was loaded for the composer
        // (and kept in sync by its saves), so a plain save against it succeeds.
        const videoDataHash = getVideoDataHash(getState());
        if (videoDataHash === null) {
            throw new Error('No video data hash found for the placeholder video.');
        }

        await persistLuminaryIntoVideo({
            luminary,
            videoId,
            videoDataHash,
            brandKitId,
            dispatch,
            getState,
        });

        // The full-update body cannot carry a name; rename separately, and
        // re-seed the data hash in case the rename bumped it server-side.
        const renameResponse = await ApiService.videoPartialUpdate({
            path: { video_id: videoId.toString() },
            body: { name },
        });
        if (renameResponse.data.video_data_hash) {
            dispatch(setVideoDataHash({ videoDataHash: renameResponse.data.video_data_hash }));
        }
    };
}

type DocumentWithViewTransition = Document & {
    startViewTransition?: (cb: () => void) => { ready: Promise<void>; finished: Promise<void> };
};

// Runs `swap` (the DOM change that replaces the build preview with the editor)
// inside a View Transition so the shared-named scene tiles morph into their
// editor scenes. flushSync forces React to apply the redux-driven swap
// synchronously inside the callback, which the View Transition API requires to
// capture the "after" state. Falls back to a plain swap where the API is absent,
// the user prefers reduced motion, or the tab is backgrounded (some browsers,
// e.g. Firefox, throw InvalidStateError synchronously from startViewTransition()
// when document.hidden is true instead of only rejecting the transition's
// promises, which would otherwise skip `swap` entirely and strand the handoff).
function handOffToEditorWithViewTransition(swap: () => void) {
    return async (dispatch: AppDispatch): Promise<void> => {
        const doc = document as DocumentWithViewTransition;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (typeof doc.startViewTransition !== 'function' || prefersReducedMotion || doc.hidden) {
            swap();
            return;
        }

        // Flush the handoff flag synchronously so the masonry tiles have their
        // view-transition-name in the DOM before startViewTransition snapshots the
        // "before" state. This thunk runs after awaits, so without flushSync the
        // re-render is batched and the snapshot would miss the names — the tiles
        // would then cross-fade against the root instead of morphing per scene.
        flushSync(() => dispatch(setSceneHandoffActive(true)));
        try {
            const transition = doc.startViewTransition(() => {
                flushSync(swap);
            });
            // `ready` rejects independently of `finished` (e.g. Chrome's
            // "Prepaint layout check failed" InvalidStateError when the DOM
            // mutated by `swap` doesn't match what the transition expects) — left
            // unhandled, that is a separate unhandled promise rejection even
            // though `finished` below is caught. The swap itself still applied,
            // so a skipped animation is a non-issue either way.
            transition.ready.catch(() => {});
            // `finished` rejects if the transition is interrupted (e.g. another
            // transition starts). The swap itself still applied, so treat an
            // interruption as a successful, just-not-animated handoff.
            await transition.finished.catch(() => {});
        } finally {
            dispatch(setSceneHandoffActive(false));
        }
    };
}

// The outcome of a turn-end handoff attempt, so the caller can decide whether to
// treat the turn as handed off (and stop retrying) or leave the door open for a
// later attempt.
//   'handed-off'    — the video was persisted and the editor is now showing it.
//   'video-exists'  — a video already existed; nothing to do, treat as done.
//   'nothing-built' — the luminary was still empty, so there was nothing to
//                     persist yet; a later turn should try again.
//   'failed'        — creating/persisting the video threw; the user is stranded
//                     on the generating preview and a retry should be allowed.
//   'limit-reached' — the backend rejected the create because the team is over its
//                     free-plan video limit; the upgrade modal was shown. This is an
//                     expected, user-actionable dead-end (not a bug), so callers
//                     should not retry it or report it as a stuck preview.
export type TurnEndHandoffOutcome =
    | 'handed-off'
    | 'video-exists'
    | 'nothing-built'
    | 'failed'
    | 'limit-reached';

// Called at the end of an agent turn in single-video sketch-in-creator mode.
// create_blank_video builds the video on the editor luminary but cannot persist
// it immediately (the backend rejects an empty video). Once the turn ends and
// the agent has added scenes, this persists the video and sets it as the app's
// current video so autosave takes over: normally by creating the DB video, or —
// when the script-composer hand-off parked a placeholder video id — by saving
// into that existing video. No-op if a video already exists or the luminary is
// still empty (e.g. the agent created nothing this turn).
export function createSingleVideoAtTurnEndOperation(luminary: Luminary) {
    return async (
        dispatch: AppDispatch,
        getState: GetRootState,
    ): Promise<TurnEndHandoffOutcome> => {
        // Lets the catch below tell "the video exists but a later step failed"
        // apart from "there is no video at all".
        let createdVideoId: number | null = null;

        // Setting the video id swaps the toolbox build preview (the masonry of
        // scene tiles) for the real editor scene list. Run that swap inside a
        // View Transition so each build-preview tile morphs into its matching
        // editor scene instead of hard-cutting. The handoff flag turns on the
        // shared view-transition-names on both sides for the duration.
        const adoptVideoIntoEditor = async (videoId: number): Promise<void> => {
            await dispatch(
                handOffToEditorWithViewTransition(() =>
                    dispatch(setVideoIdAndHookupAnalytics(videoId)),
                ),
            );

            // The placeholder (if any) is now the app's current video; clear the
            // parked id so nothing tries to adopt it again.
            dispatch(setScriptComposerPlaceholderVideoId(null));

            dispatch(clearAllAgentActivity());

            // Reflect the new video in the URL (?id=X) so a refresh / shared link
            // reopens it, matching the normal create flow. Drop the now-stale
            // skip_script_composer handoff param so a refresh loads the video
            // instead of re-entering the empty chat/creator state.
            let newUrl = QueryStringManipulator.updateQueryStringParameter(
                window.location.href,
                'id',
                videoId.toString(),
            );
            newUrl = QueryStringManipulator.removeQueryStringParameter(
                newUrl,
                SKIP_SCRIPT_COMPOSER_PARAM,
            );
            window.history.replaceState({ path: newUrl }, '', newUrl);
        };

        try {
            if (getVideoId(getState()) != null) {
                return 'video-exists';
            }
            if (luminary.sceneGroups.length === 0) {
                return 'nothing-built';
            }

            const name = getVideoName(getState()) || 'Untitled video';
            const brandKitId = selectSketchControlsBrandKitId(getState());
            const placeholderVideoId = getScriptComposerPlaceholderVideoId(getState());
            let videoId: number;
            // Both branches record the video id as soon as the row exists, so a
            // later failure hands off to the editor (where autosave retries)
            // instead of stranding the user on the preview and orphaning it.
            if (placeholderVideoId != null) {
                videoId = placeholderVideoId;
                createdVideoId = placeholderVideoId;
                await dispatch(
                    adoptScriptComposerPlaceholderVideoOperation({
                        luminary,
                        videoId,
                        name,
                        brandKitId,
                    }),
                );
            } else {
                videoId = await dispatch(
                    createVideoFromLuminaryOperation({
                        luminary,
                        name,
                        brandKitId,
                        onVideoCreated: (id) => {
                            createdVideoId = id;
                        },
                    }),
                );
            }

            await adoptVideoIntoEditor(videoId);
            return 'handed-off';
        } catch (error) {
            // The team hit its free-plan video limit: the client-side gate can be
            // stale (another tab, or videos created earlier this session), so the
            // backend 403 is the real source of truth. Surface the upgrade modal —
            // same as every other video-creating path — instead of a generic error
            // toast, and leave the built scenes on the generating preview so they
            // convert-then-keep. Not a bug, so don't report it to Sentry.
            if (handleVideoLimitError(error, dispatch)) {
                return 'limit-reached';
            }
            // The video exists and only a later step failed, so adopt it anyway:
            // retrying would orphan it and burn another video from the team's
            // quota, and the editor's autosave will retry the save that failed.
            if (createdVideoId != null) {
                ErrorReporter.capture(error);
                await adoptVideoIntoEditor(createdVideoId);
                return 'handed-off';
            }
            // No video was ever created (e.g. API/network error, or a lost WebGL
            // context tripping the onCanPlay timeout). Report it and tell the user,
            // rather than leaving them on the generating preview with no feedback.
            // The scenes remain on the editor luminary, so a retry (this turn's
            // fallback or the next turn's save) can recreate the video.
            ErrorReporter.capture(error);
            Toast.error('Something went wrong creating your video, please try again.');
            return 'failed';
        } finally {
            // The turn has settled: stop showing the immediate "creating" loading
            // view. A parked placeholder id is deliberately NOT cleared here: if
            // this turn failed or built nothing, a later turn should still adopt
            // the placeholder rather than orphan it with a brand-new video.
            dispatch(setToolboxCreatingVideo(false));
        }
    };
}

async function saveVideoDraftToDatabase(
    videoDraftId: string,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<number> {
    const draft = getVideoDraftById(getState(), videoDraftId);
    if (!draft) {
        throw new Error('Missing video draft');
    }

    const luminary = draft.luminary;

    // Wait for Luminary to finish loading before accessing template.id
    // (template.id flickers between a UUID and the actual ID during initialization)
    await luminary.onCanPlay();

    const videoData = luminary.save();
    const currentHash = hashVideoData(JSON.stringify(videoData));

    // Return cached video ID if the video data hasn't changed
    if (draft.savedVideoId && draft.savedVideoDataHash === currentHash) {
        return draft.savedVideoId;
    }

    const response = await ApiService.videoCreate({
        body: {
            video_data: videoData,
            aspect_ratio: luminary.aspectRatio,
            template: luminary.template.id,
            script_composer_data: null,
            name: draft.name,
            creation_source: CreationSourceEnum.SKETCH,
        },
    });
    const video_id = response.data.video_id;

    await ApiService.videoUpdate({
        path: { video_id: video_id.toString() },
        body: {
            video_data: luminary.save(),
            script: parseScript(luminary),
            blueprint_id: null,
            transcripts: getTranscriptsToSave(getState()),
            narrative_threads: getNarrativeThreads(luminary),
            duration_seconds: Math.floor(luminary.duration / 1000),
            user_overrides: {},
            user_uploads: [],
            brand_kit_id: draft.brandKitId ?? null,
            template_id: luminary.template.id,
            aspect_ratio: luminary.aspectRatio,
            video_data_hash: response.data.video_data_hash,
            video_updated_by_user: false,
            video_init_type: VIDEO_INIT_TYPE.CREATE_FROM_SCRATCH,
            script_composer_data: null,
            article_original_content: null,
            tab_durations: null,
            render: false,
            async_generation_id: null,
            async_generation_data: null,
        },
    });

    dispatch(
        setVideoDraftSavedVideoId({
            id: videoDraftId,
            savedVideoId: video_id,
            savedVideoDataHash: currentHash,
        }),
    );

    return video_id;
}

async function fetchTemplateStylingVideoData(
    videoPlan: VideoPlan,
): Promise<OpaqueVideoData | null> {
    try {
        if (!videoPlan.blueprint_id) {
            return null;
        }

        const response = await ApiService.blueprintsTemplateStyleVideoDataRetrieve({
            path: {
                id: videoPlan.blueprint_id,
            },
            query: {
                template: videoPlan.template_id,
            },
        });
        return JSON.parse(response.data.video_data) as OpaqueVideoData;
    } catch (error) {
        ErrorReporter.capture(error);
        return null;
    }
}

async function fetchSceneAttributesForVideo(
    creationData: VideoPlanCreationData,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<void> {
    try {
        const dataLuminary = new Luminary({
            fetchResources: false,
            disableRenderer: true,
            autoDraw: false,
            autoUpdate: false,
        });

        await loadVideoPlanIntoLuminary(dataLuminary, creationData, dispatch, getState);

        const { fetchAndSetRecommendedSceneAttributes } = makeOperationsCallable(
            { fetchAndSetRecommendedSceneAttributesOperation },
            dataLuminary,
            dispatch,
            getState,
        );

        const promises = dataLuminary.scenes.map((scene) => {
            return fetchAndSetRecommendedSceneAttributes({
                sceneId: scene.id,
                isGlobalVideo: false,
            });
        });
        await Promise.all(promises);

        dataLuminary.destroy();
    } catch (error) {
        ErrorReporter.capture(error);
    }
}

/**
 * Generate music recommendation for a video plan.
 *
 * Requires either sceneRecommendations OR scriptText to be provided.
 *
 * @param videoPlan - The video plan to generate music for.
 * @param sceneRecommendations - The scene recommendations to use for music generation.
 * @param scriptText - Optional script text to use for music generation. If provided, will generate
 *                     music from this script instead of waiting for scene recommendations.
 * @param getState - Redux state getter.
 */
async function fetchMusicRecommendation(
    videoPlan: VideoPlan,
    getState: GetRootState,
    sceneRecommendations?: SceneRecommendation[],
    scriptText?: string,
): Promise<AudioAssetModel | null> {
    try {
        const minDuration = videoPlan.duration_target_seconds || 60;

        if (sceneRecommendations) {
            const { sentences } = createBestGuessTranscript(sceneRecommendations);
            scriptText = sentences.join(' ');
        }

        if (!scriptText)
            throw new Error('No script text provided for music recommendation generation');

        const musicRecommendationMode = selectMusicRecommendationMode(getState());

        const taskResponse = await ApiService.musicRecommendationCreate({
            body: {
                text: scriptText,
                min_duration: minDuration,
                music_recommendation_mode: musicRecommendationMode,
            },
        });
        const ws = WebSocketClient.getInstance();
        const tracks = await ws.waitForMessage<AudioAssetModel[]>(
            taskResponse.data.task_id,
            TaskResultMessageTypeEnum.TASK_RESULT,
            'music_recommendation',
        );
        return tracks[0] ?? null;
    } catch (error) {
        ErrorReporter.capture(error);
        return null;
    }
}

/**
 * Generate the AI voiceover for a video plan. If it already is loading, this will do nothing.
 *
 * Requires either sceneRecommendations OR scriptText + language to be provided.
 *
 * @param videoPlan - The video plan to generate voiceover for.
 * @param sceneRecommendations - The scene recommendations to use for voiceover generation.
 * @param scriptText - Optional script text to use for voiceover generation. If provided, will generate
 *                     voiceover from this script instead of waiting for scene recommendations.
 * @param language - Optional language code. Required if scriptText is provided.
 */
async function _generateVoiceoverForVideoPlan(
    videoPlan: VideoPlan,
    sceneRecommendations?: SceneRecommendation[],
    scriptText?: string,
    language?: LanguageEnum,
): Promise<{ voiceoverAsset: AudioAssetModel; transcript: AssetTranscript } | null> {
    if (!videoPlan.ai_voice) {
        throw new Error('No AI voice selected for voiceover generation');
    }

    if (sceneRecommendations) {
        // populate scriptText and language from scene recommendations
        const hasFullyLoadedRecommendations = sceneRecommendations.every((s) => s !== null);
        if (!hasFullyLoadedRecommendations) {
            throw new Error(
                'Scene recommendations are not fully loaded, cannot generate voiceover',
            );
        }
        const { sentences } = createBestGuessTranscript(sceneRecommendations);
        scriptText = sentences.join(' ');
        language = sceneRecommendations[0].scene_data.language as LanguageEnum;
    }

    if (!scriptText) throw new Error('No script text provided for voiceover generation');
    if (!language) throw new Error('No language provided for voiceover generation');

    const { voiceoverAsset, transcript } = await createRecommendationAIVoiceover({
        script: scriptText,
        language,
        voiceId: videoPlan.ai_voice,
    });

    return {
        voiceoverAsset: voiceoverAsset.toJS(),
        transcript,
    };
}

async function generateThumbnailPreview(
    videoDraftId: string,
    creationData: VideoPlanCreationData,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<void> {
    if (
        creationData.sceneRecommendations.length === 0 ||
        creationData.sceneRecommendations[0] === null
    ) {
        throw new Error('No scene recommendations for thumbnail');
    }

    const firstSceneOnly: VideoPlanCreationData = {
        ...creationData,
        sceneRecommendations: [creationData.sceneRecommendations[0]],
    };

    try {
        const luminary = new Luminary({
            ...DEFAULT_LUMINARY_CONFIG,
            resolutionValue: 400,
            fetchResources: {
                audio: false,
                media: true,
                transitions: false,
                fonts: true,
            },
        });

        await loadVideoPlanIntoLuminary(luminary, firstSceneOnly, dispatch, getState);

        const { fetchAndSetRecommendedSceneAttributes } = makeOperationsCallable(
            { fetchAndSetRecommendedSceneAttributesOperation },
            luminary,
            dispatch,
            getState,
        );

        try {
            await fetchAndSetRecommendedSceneAttributes({
                sceneId: luminary.scenes[0].id,
                isGlobalVideo: false,
            });
        } catch (e) {
            ErrorReporter.capture(e);
        }

        await luminary.onAssetsDoneTryingToLoad();

        const canvas = document.createElement('canvas');
        const { width, height } = VideoData.getResolution(
            luminary.resolutionValue,
            luminary.aspectRatio,
        );
        canvas.width = width;
        canvas.height = height;

        await luminary.scenes[0].renderPreviewToCanvas(canvas);

        dispatch(
            setVideoDraftPreviewCanvas({
                id: videoDraftId,
                canvas: canvas,
            }),
        );

        luminary.destroy();
    } catch (e) {
        ErrorReporter.capture(e);
    }
}

/**
 * Generates a thumbnail preview from an existing video draft's Luminary instance.
 * This is useful for agent-built videos where we already have a fully populated
 * Luminary instance and just need to capture a screenshot of the first scene.
 *
 * @param videoDraftId - The ID of the video draft to generate thumbnail for
 */
export function generateThumbnailFromVideoDraftOperation(videoDraftId: string) {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<void> => {
        const draft = getVideoDraftById(getState(), videoDraftId);
        if (!draft) {
            ErrorReporter.capture(new Error('Video draft not found for thumbnail generation'), {
                extra: { videoDraftId },
            });
            return;
        }

        const luminary = draft.luminary;
        if (!luminary || luminary.scenes.length === 0) {
            // A blank-video draft that the agent never populated with scenes has
            // nothing to thumbnail yet. This is an expected state (e.g. a turn
            // ends before any scene is added), not an error worth reporting.
            return;
        }

        try {
            // Wait for assets to load before rendering
            await luminary.onAssetsDoneTryingToLoad();

            const canvas = document.createElement('canvas');
            const { width, height } = VideoData.getResolution(
                luminary.resolutionValue,
                luminary.aspectRatio,
            );
            canvas.width = width;
            canvas.height = height;

            await luminary.scenes[0].renderPreviewToCanvas(canvas);

            dispatch(
                setVideoDraftPreviewCanvas({
                    id: videoDraftId,
                    canvas: canvas,
                }),
            );
        } catch (e) {
            ErrorReporter.capture(e, {
                extra: { videoDraftId, context: 'generateThumbnailFromVideoDraft' },
            });
        }
    };
}

/**
 * Resolves the busy state of every blank-video draft that is still marked as
 * generating, and refreshes each one's thumbnail. Called when an agent turn
 * ends: blank-video drafts stay isGenerating while the agent builds them up, so
 * this is what stops the tool-call header spinner once the turn completes.
 *
 * The busy-state clear is dispatched first and unconditionally; the thumbnail
 * refresh is best-effort and must never block or revert the clear. Returns the
 * ids that were cleared (useful for tests and callers).
 */
export function clearGeneratingBlankVideoDraftsOperation() {
    return (dispatch: AppDispatch, getState: GetRootState): string[] => {
        const idsToClear = selectVideoDrafts(getState())
            .filter(
                (draft) => draft.creationToolName === 'create_blank_video' && draft.isGenerating,
            )
            .map((draft) => draft.id);
        idsToClear.forEach((id) => {
            dispatch(setVideoDraftGenerating({ id, isGenerating: false }));
            void dispatch(generateThumbnailFromVideoDraftOperation(id));
        });
        return idsToClear;
    };
}

const FIRST_SCENE_PREVIEW_REFRESH_DEBOUNCE_MS = 500;
let firstScenePreviewRefreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Debounced refresh of the selected blank-video draft's thumbnail after its
 * first scene (index 0) changes mid-turn. The editing tools call this when they
 * successfully edit scene index 0 so the tile preview tracks the live video,
 * not just the turn-end snapshot. A burst of scene-0 edits coalesces into a
 * single render. No-ops when the selected draft is not a blank video.
 *
 * Exported for tests via __resetFirstScenePreviewRefresh.
 */
export function refreshFirstScenePreviewOperation() {
    return (dispatch: AppDispatch, getState: GetRootState): void => {
        const selectedId = selectSelectedVideoDraft(getState());
        if (!selectedId) {
            return;
        }
        const draft = getVideoDraftById(getState(), selectedId);
        if (!draft || draft.creationToolName !== 'create_blank_video') {
            return;
        }
        if (firstScenePreviewRefreshTimer) {
            clearTimeout(firstScenePreviewRefreshTimer);
        }
        firstScenePreviewRefreshTimer = setTimeout(() => {
            firstScenePreviewRefreshTimer = null;
            void dispatch(generateThumbnailFromVideoDraftOperation(selectedId));
        }, FIRST_SCENE_PREVIEW_REFRESH_DEBOUNCE_MS);
    };
}

/** Test helper: cancels any pending debounced first-scene preview refresh. */
export function __resetFirstScenePreviewRefresh(): void {
    if (firstScenePreviewRefreshTimer) {
        clearTimeout(firstScenePreviewRefreshTimer);
        firstScenePreviewRefreshTimer = null;
    }
}

/**
 * Generate a script from a content prompt using the GenerateScript backend endpoint.
 * Calls the backend, waits for the async task result via WebSocket, and returns
 * the script as a plain string.
 */
export function generateScriptForVideoPlan({
    videoPlan,
    referenceMaterial,
}: {
    videoPlan: VideoPlan;
    referenceMaterial: string;
}) {
    return async (dispatch: AppDispatch, _getState: GetRootState): Promise<string> => {
        let targetDuration = videoPlan.duration_target_seconds;
        if (!targetDuration) {
            const blueprint = videoPlan.blueprint_id
                ? await dispatch(getOrFetchBlueprint(videoPlan.blueprint_id))
                : null;
            const isUsingCustomerBlueprint = !blueprint?.is_public;
            if (blueprint && isUsingCustomerBlueprint) {
                // If in a customer blueprint, fallback to the blueprint target duration
                targetDuration = blueprint.default_target_duration_seconds;
            } else {
                targetDuration = DurationTargetDefaults.DEFAULT;
            }
        }
        const response = await ApiService.generateScriptCreate({
            body: {
                doctext: referenceMaterial,
                title: videoPlan.title,
                description: videoPlan.content_prompt ?? '',
                narrative_thread: videoPlan.narrative_thread,
                duration: targetDuration,
                blueprint_id: videoPlan.blueprint_id ?? undefined,
                template_id: videoPlan.template_id,
            },
        });

        const ws = WebSocketClient.getInstance();
        const scriptResponse = await ws.waitForMessage<GenerateScriptResponse>(
            response.data.task_id,
            TaskResultMessageTypeEnum.TASK_RESULT,
            'generate_script',
        );

        const scriptText = scriptResponse.data.script
            .map((block) => block.sentences.join(' '))
            .join('\n\n');
        return scriptText;
    };
}

/**
 * Builds a blank, scene-less but editable Luminary: applies the template and
 * its capabilities and sets the aspect ratio. No scenes are added — the agent
 * builds the video up from empty with the edit tools (add_scene, etc.). A
 * scene-less Luminary is a valid in-memory state; the edit tools that persist
 * (e.g. set_aspect_ratio) simply skip saving until at least one scene exists.
 *
 * This mirrors the editable-prefix of `loadVideoPlanIntoLuminary` without any
 * server-driven script/scene generation.
 */
// Fetch the FULL template record and load its scene designs, then apply it (and
// the requested aspect ratio) to the given luminary in place. Returns once the
// luminary is a blank, ready-to-edit video on that template.
//
// The full template fetch overwrites whatever is in the ORM: the lightweight
// list-loaded record can omit fields the editing tools need (e.g.
// default_voiceover_scene_design, which gates voiceover scene groups), and
// loadTemplateAndSceneDesigns skips the detail fetch when any record for the id
// already exists.
export function applyBlankTemplateToLuminary({
    luminary,
    templateId,
    aspectRatio,
}: {
    luminary: Luminary;
    templateId: string;
    aspectRatio: AspectRatio;
}) {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<void> => {
        const templateResponse = await ApiService.templatesRetrieve({ path: { id: templateId } });
        dispatch(storeTemplate({ template: templateResponse.data }));

        // Loads scene designs and display groups (template fetch is now a no-op).
        await dispatch(loadTemplateAndSceneDesigns(templateId));

        const templateBundle = getTemplateBundle(getState(), templateId);
        if (!templateBundle) {
            throw new Error('Template bundle not found');
        }

        applyTemplateAndCapabilities(luminary, templateBundle, dispatch, getState);

        luminary.aspectRatio = aspectRatio;
    };
}

// Switches the current editor video to a different template in place (same video id/tab). Autosave
// persists the change, so there's no backend step; pass the current template id to "start over".
// The template loads before the luminary is touched, and clear()→addScene() run with no await
// between, so autosave never catches the video scene-less (which also sidesteps the last-scene
// delete guard).
/** A voice-driven asset (voiceover audio or talking-head recording) lifted out of a video before a
 *  template rebuild, with the full transcript needed to reconstruct it under the new template. */
interface CapturedVoicedrivenBackbone {
    isTalkingHead: boolean;
    assetSourceData: AudioSourceData | MediaSourceData;
    transcript: AssetTranscript;
    playbackRegions: [number, number][];
}

/**
 * Capture each DISTINCT voice-driven asset in the video (keyed by the underlying file, so groups that
 * share one asset — e.g. after split_scene_group — collapse to a single backbone with their transcripts
 * reunited in scene-group order). Guarantees at least one recreatable group per voice-driven asset.
 *
 * Fully in-memory: reads the loaded asset + `originalTranscript`, so it needs no backend round-trip and
 * preserves the exact original word timings. `originalTranscript.words` are in ms; `AssetTranscript`
 * words are in seconds (the apply operations run convertTranscriptWordsToMillis), so convert ms -> s.
 */
function captureVoicedrivenBackbones(luminary: Luminary): CapturedVoicedrivenBackbone[] {
    const byAsset = new Map<string, CapturedVoicedrivenBackbone>();
    luminary.sceneGroups.forEach((sceneGroup, index) => {
        if (!isVoiceDrivenSceneGroup(sceneGroup)) {
            return;
        }
        const isTalkingHead = isTalkingHeadSceneGroup(sceneGroup);
        const asset = isTalkingHead ? sceneGroup.mediaAsset : sceneGroup.voiceoverAsset;
        if (!asset) {
            return;
        }
        const assetSourceData = asset.sourceData;
        if (!assetSourceData) {
            return;
        }
        // Cast because luminary's transcript-word shape is structurally compatible with the client
        // TranscriptWord the apply path expects.
        const words = (sceneGroup.originalTranscript?.words ?? []).map((word) => ({
            ...word,
            startTime: word.startTime / 1000,
            endTime: word.endTime / 1000,
        })) as AssetTranscript['transcriptWords'];
        const playbackRegions = (asset.playbackRegions ?? []).map(
            ([startTime, endTime]) => [startTime, endTime] as [number, number],
        );
        const key = assetSourceData.source_id ?? assetSourceData.uuid ?? `__group_${index}`;
        const existing = byAsset.get(key);
        if (existing) {
            existing.transcript.transcriptWords.push(...words);
            existing.playbackRegions.push(...playbackRegions);
        } else {
            byAsset.set(key, {
                isTalkingHead,
                assetSourceData,
                playbackRegions,
                transcript: {
                    hasVoiceover: true,
                    transcriptWords: words,
                    language:
                        (sceneGroup.originalTranscript?.language as LanguageEnum) ??
                        LanguageEnum.EN,
                },
            });
        }
    });
    return [...byAsset.values()];
}

/** Re-apply the captured trim to a just-recreated voice-driven scene group. The recreate path reattaches
 *  the full source recording, so without this the clip the user made at insertion time is lost on rebuild. */
function reapplyPlaybackRegions(
    backbone: CapturedVoicedrivenBackbone,
    luminary: Luminary,
    sceneGroupId: SceneGroup['id'],
    applyPlaybackRegionsToSceneGroup: (
        sceneGroup: SceneGroup,
        playbackRegions: [number, number][],
    ) => void,
): void {
    if (!backbone.playbackRegions.length) {
        return;
    }
    const recreatedSceneGroup = luminary.sceneGroups.find(
        (sceneGroup) => sceneGroup.id === sceneGroupId,
    );
    if (recreatedSceneGroup) {
        applyPlaybackRegionsToSceneGroup(recreatedSceneGroup, backbone.playbackRegions);
    }
}

/** Rebuild a preserved voiceover asset as a voice-driven scene group in the (already-switched) template,
 *  reusing the identical audio and exact transcript — no regeneration, no backend round-trip.
 *  Mirrors add_ai_voiceover's create+apply, with recommendOverlays off (overlay recommendation is the
 *  legacy flow and also hits the backend): the agent assigns designs/overlays during the rebuild. */
async function recreateVoiceoverBackbone(
    backbone: CapturedVoicedrivenBackbone,
    luminary: Luminary,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<void> {
    // dynamic import to avoid a circular dependency (clip-creator-operations pulls in the Toast/error
    // chain, which cycles back through this reducer-graph module at init time).
    const { applyPlaybackRegionsToSceneGroupOperation } =
        await import('js/src/data/clip-creator/clip-creator-operations');
    const {
        createNewVoicedrivenSceneGroup,
        setAndApplyVoiceoverAssetForSceneGroup,
        applyPlaybackRegionsToSceneGroup,
    } = makeOperationsCallable(
        {
            createNewVoicedrivenSceneGroupOperation,
            setAndApplyVoiceoverAssetForSceneGroupOperation,
            applyPlaybackRegionsToSceneGroupOperation,
        },
        luminary,
        dispatch,
        getState,
    );
    const parentActionGroup = 'rebuild-video-preserve-voicedriven';
    const { sceneGroupId } = createNewVoicedrivenSceneGroup(
        VideoData.CATEGORY_VOICEOVER,
        luminary.sceneGroups.length,
        { deleteOtherScenes: false, setPlaceholderAsset: false, hasAvatar: false },
        parentActionGroup,
        true,
    );
    // setAndApplyVoiceoverAssetForSceneGroup rejects an audio asset that doesn't declare has_voiceover
    // or that has no uuid. Generated AI-voiceover assets can lack both in the serialized source data, so
    // backfill them (uuid falls back to the storage path). We supply the transcript, so the uuid-keyed
    // transcript fetch is skipped — the uuid is only used as an identity/existence key here.
    const audioAssetData = ImmutableFromJS({
        ...backbone.assetSourceData,
        has_voiceover: true,
        uuid: backbone.assetSourceData.uuid ?? backbone.assetSourceData.source_id ?? undefined,
    }) as ImmutableMap<AudioAssetModel>;
    await setAndApplyVoiceoverAssetForSceneGroup({
        sceneGroupId,
        audioAssetData,
        transcript: backbone.transcript,
        avatarAssetData: null,
        avatarBackgroundAssetData: null,
        captionsLanguage: backbone.transcript.language,
        forceCaptionGeneration: false,
        selectedBlueprintId: null,
        generateRecommendedVideoResponse: null,
        sceneRecommendations: null,
        recommendOverlays: false,
        isGlobalVideo: true,
        systemTriggered: true,
        parentActionGroup,
    });
    reapplyPlaybackRegions(backbone, luminary, sceneGroupId, applyPlaybackRegionsToSceneGroup);
}

/** Rebuild a preserved talking-head recording as a talking-head scene group in the (already-switched)
 *  template. Passes the captured transcript so applyTalkingHeadMedia skips STT + the cached-transcript
 *  fetch (both need the asset in the backend) and preserves the original word timings. */
async function recreateTalkingHeadBackbone(
    backbone: CapturedVoicedrivenBackbone,
    luminary: Luminary,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<void> {
    // dynamic import to avoid a circular dependency (clip-creator-operations pulls in the Toast/error
    // chain, which cycles back through this reducer-graph module at init time).
    const { applyPlaybackRegionsToSceneGroupOperation } =
        await import('js/src/data/clip-creator/clip-creator-operations');
    const {
        createNewVoicedrivenSceneGroup,
        applyTalkingHeadMedia,
        applyPlaybackRegionsToSceneGroup,
    } = makeOperationsCallable(
        {
            createNewVoicedrivenSceneGroupOperation,
            applyTalkingHeadMediaOperation,
            applyPlaybackRegionsToSceneGroupOperation,
        },
        luminary,
        dispatch,
        getState,
    );
    const parentActionGroup = 'rebuild-video-preserve-voicedriven';
    const { sceneGroupId } = createNewVoicedrivenSceneGroup(
        VideoData.CATEGORY_TALKING_HEAD,
        luminary.sceneGroups.length,
        { deleteOtherScenes: false, setPlaceholderAsset: false, hasAvatar: false },
        parentActionGroup,
        true,
    );
    // Reconstruct the background from the captured recording so applyTalkingHeadMedia can attach it as
    // the on-camera media (it reads the video's uuid + source data off this).
    const media = ImmutableFromJS({
        type: SlideBackgroundModelTypeEnum.VIDEO,
        video: backbone.assetSourceData,
        image: null,
    }) as unknown as ImmutableMap<SlideBackgroundModel>;
    await applyTalkingHeadMedia({
        sceneGroupId,
        media,
        transcript: backbone.transcript,
        recommendOverlays: false,
        enhanceAudio: false,
        trimRequired: false,
        systemTriggered: true,
        parentActionGroup,
    });
    reapplyPlaybackRegions(backbone, luminary, sceneGroupId, applyPlaybackRegionsToSceneGroup);
}

export function switchLuminaryTemplateInPlace({
    luminary,
    templateId,
    aspectRatio,
}: {
    luminary: Luminary;
    templateId: string;
    aspectRatio: AspectRatio;
}) {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<void> => {
        const templateResponse = await ApiService.templatesRetrieve({ path: { id: templateId } });
        dispatch(storeTemplate({ template: templateResponse.data }));
        await dispatch(loadTemplateAndSceneDesigns(templateId));

        const templateBundle = getTemplateBundle(getState(), templateId);
        if (!templateBundle) {
            throw new Error('Template bundle not found');
        }
        const defaultSceneDesignId =
            getTemplateDefaultStoryboardSceneDesign(getState(), templateId) ??
            getTemplateDefaultSceneDesign(getState(), templateId);
        if (!defaultSceneDesignId) {
            throw new Error('Default scene design not found for template');
        }

        // Music isn't template-specific — carry it across clear(), which would otherwise drop it.
        const preservedMusic = luminary.music;
        const preservedMusicVolume = luminary.musicAsset?.volume;

        // Voice-driven narration (AI/recorded voiceover, talking head) is the spine of the video and
        // can't be regenerated faithfully from a text description — capture each distinct asset + its
        // transcript before clear() so the narration can be rebuilt under the new template rather than
        // dropped (which would force the agent to regenerate it, losing the original audio/voice).
        const voicedrivenBackbones = captureVoicedrivenBackbones(luminary);

        await luminary.clear();
        applyTemplateAndCapabilities(luminary, templateBundle, dispatch, getState);
        luminary.aspectRatio = aspectRatio;

        // Recreate one voice-driven scene group per preserved asset in the new template, reusing the
        // identical audio/recording + exact transcript so the narration survives the template switch.
        for (const backbone of voicedrivenBackbones) {
            if (backbone.isTalkingHead) {
                await recreateTalkingHeadBackbone(backbone, luminary, dispatch, getState);
            } else {
                await recreateVoiceoverBackbone(backbone, luminary, dispatch, getState);
            }
        }

        // Always leave at least one scene: a pure-text rebuild (no voice-driven backbone recreated)
        // starts from a single blank scene, exactly as before.
        if (luminary.scenes.length === 0) {
            luminary.addScene({ sceneDesignId: defaultSceneDesignId });
        }

        if (preservedMusic) {
            luminary.music = preservedMusic;
            if (preservedMusicVolume != null && luminary.musicAsset) {
                luminary.musicAsset.volume = preservedMusicVolume;
            }
        }
    };
}

export function initializeBlankLuminary({
    templateId,
    aspectRatio,
}: {
    templateId: string;
    aspectRatio: AspectRatio;
}) {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<Luminary> => {
        const luminary = new Luminary({
            ...DEFAULT_LUMINARY_CONFIG,
            resolutionValue: getVideoDraftLuminaryResolution(),
        });

        await dispatch(applyBlankTemplateToLuminary({ luminary, templateId, aspectRatio }));

        return luminary;
    };
}

export function createAndPopulateVideoDraftFromVideoPlan({
    videoDraftId,
    videoPlan,
    referenceMaterial,
    sourceMediaUUIDs,
    creationToolName,
    mediaOptions,
    conversationId,
    brandKitId,
}: {
    videoDraftId: string;
    videoPlan: VideoPlan;
    referenceMaterial: string;
    sourceMediaUUIDs?: string[];
    creationToolName: VideoDraftCreationTool;
    mediaOptions?: MediaOptionsRequest;
    conversationId?: string;
    brandKitId?: number | null;
}) {
    return async (
        dispatch: AppDispatch,
        getState: GetRootState,
    ): Promise<{ success: true; taskId: string } | { success: false; errorMessage?: string }> => {
        const luminary = new Luminary({
            ...DEFAULT_LUMINARY_CONFIG,
            resolutionValue: getVideoDraftLuminaryResolution(),
        });

        // Calculate estimated loading duration: min 20 seconds, or duration_target_seconds
        const estimatedLoadingDurationSeconds = Math.max(
            20,
            videoPlan.duration_target_seconds || 0,
        );

        dispatch(
            addVideoDraft({
                id: videoDraftId,
                luminary,
                name: videoPlan.title,
                estimatedLoadingDurationSeconds,
                creationToolName,
                videoPlan,
                brandKitId,
            }),
        );

        try {
            await dispatch(loadTemplateAndSceneDesigns(videoPlan.template_id));
            if (videoPlan.blueprint_id) {
                await dispatch(fetchBlueprints({ ids: [videoPlan.blueprint_id] }));
            }

            const previewTaskQueue = new TaskQueue(2, 0);
            let hasLoadedFirstScene = false;
            let hasStartedVoiceoverGeneration = false;
            let hasStartedMusicGeneration = false;
            const containsVoiceover =
                videoPlan.narrative_thread === NarrativeThreadEnum.VOICEOVER ||
                videoPlan.narrative_thread === NarrativeThreadEnum.TALKING_HEAD;

            const stylingPromise = fetchTemplateStylingVideoData(videoPlan);

            const response = await ApiService.generateVideosForVideoPlansCreate({
                body: {
                    reference_material: referenceMaterial,
                    video_plans: [
                        {
                            title: videoPlan.title,
                            content_prompt: videoPlan.content_prompt,
                            video_prompt: videoPlan.video_prompt,
                            script: videoPlan.script,
                            duration_target_seconds: videoPlan.duration_target_seconds,
                            template_id: videoPlan.template_id,
                            narrative_thread: videoPlan.narrative_thread,
                            blueprint_id: videoPlan.blueprint_id,
                            aspect_ratio: videoPlan.aspect_ratio,
                            ai_voice: videoPlan.ai_voice,
                        },
                    ],
                    source_media_uuids: sourceMediaUUIDs,
                    media_options: mediaOptions,
                    conversation_id: conversationId,
                },
            });

            let voiceoverPromise: Promise<{
                voiceoverAsset: AudioAssetModel;
                transcript: AssetTranscript;
            } | null> = Promise.resolve(null);
            let musicPromise: Promise<AudioAssetModel | null> = Promise.resolve(null);

            const taskId = response.data.task_ids[0];
            const ws = WebSocketClient.getInstance();
            const recommendedVideo = await ws.waitForMessage<GenerateRecommendedVideoResponse>(
                taskId,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'generate_recommended_video',
                {
                    partialResponseCallback: (partialResult) => {
                        if (!hasLoadedFirstScene && partialResult.scenes?.length > 0) {
                            hasLoadedFirstScene = true;
                            previewTaskQueue.queueTask(async () => {
                                const partialCreationData: VideoPlanCreationData = {
                                    videoPlan: videoPlan,
                                    referenceMaterial,
                                    templateStylingVideoData: await stylingPromise,
                                    sceneRecommendations: partialResult.scenes,
                                    voiceoverAsset: null,
                                    voiceoverTranscript: null,
                                    musicAsset: null,
                                    brandKitId: brandKitId ?? null,
                                };
                                await generateThumbnailPreview(
                                    videoDraftId,
                                    partialCreationData,
                                    dispatch,
                                    getState,
                                );
                            });
                        }
                        if (
                            containsVoiceover &&
                            !hasStartedVoiceoverGeneration &&
                            partialResult.script
                        ) {
                            // start voiceover generation once we have a script
                            hasStartedVoiceoverGeneration = true;
                            const language = partialResult.scenes[0]?.scene_data
                                ?.language as LanguageEnum;
                            if (language) {
                                voiceoverPromise = _generateVoiceoverForVideoPlan(
                                    videoPlan,
                                    undefined,
                                    partialResult.script,
                                    language,
                                );
                            }
                        }
                        if (!hasStartedMusicGeneration && partialResult.script) {
                            // start music generation once we have a script
                            hasStartedMusicGeneration = true;
                            musicPromise = fetchMusicRecommendation(
                                videoPlan,
                                getState,
                                undefined,
                                partialResult.script,
                            );
                        }
                    },
                },
            );

            const templateStylingVideoData = await stylingPromise;
            const sceneRecommendations = recommendedVideo.scenes;

            if (containsVoiceover && !hasStartedVoiceoverGeneration) {
                voiceoverPromise = _generateVoiceoverForVideoPlan(videoPlan, sceneRecommendations);
            }

            if (!hasStartedMusicGeneration) {
                musicPromise = fetchMusicRecommendation(videoPlan, getState, sceneRecommendations);
            }

            const creationData: VideoPlanCreationData = {
                videoPlan,
                referenceMaterial,
                templateStylingVideoData,
                sceneRecommendations,
                voiceoverAsset: null,
                voiceoverTranscript: null,
                musicAsset: null,
                brandKitId: brandKitId ?? null,
            };

            const attrRecProm = fetchSceneAttributesForVideo(creationData, dispatch, getState);

            let thumbProm = Promise.resolve();
            if (!hasLoadedFirstScene) {
                thumbProm = previewTaskQueue.queueTask(async () => {
                    await generateThumbnailPreview(videoDraftId, creationData, dispatch, getState);
                });
            }

            const [musicAsset, voiceoverResult] = await Promise.all([
                musicPromise,
                voiceoverPromise,
                thumbProm,
                attrRecProm,
            ]);
            if (musicAsset) {
                creationData.musicAsset = musicAsset;
            }
            if (voiceoverResult) {
                creationData.voiceoverAsset = voiceoverResult.voiceoverAsset;
                creationData.voiceoverTranscript = voiceoverResult.transcript;
            }

            await loadVideoPlanIntoLuminary(luminary, creationData, dispatch, getState);

            dispatch(
                setVideoDraftGenerating({
                    id: videoDraftId,
                    isGenerating: false,
                }),
            );

            return { success: true, taskId };
        } catch (error) {
            ErrorReporter.capture(error);
            const errorMessage = isErrorObject(error)
                ? error.error.message
                : error instanceof Error
                  ? error.message
                  : 'Failed to generate video';
            dispatch(
                setVideoDraftError({
                    id: videoDraftId,
                    error_message: errorMessage,
                }),
            );
            dispatch(
                setVideoDraftGenerating({
                    id: videoDraftId,
                    isGenerating: false,
                }),
            );
            return { success: false, errorMessage };
        }
    };
}

// Same as createAndPopulateVideoDraftFromVideoPlan, but for creating a video
// from a voiceover asset (i.e. uploaded voiceover audio or a talking-head video
// clip). Skips the backend video-plan / script-generation pipeline and instead
// builds the Luminary tree directly from the uploaded asset via the
// voicedriven scene-group operations.
export function createAndPopulateVideoDraftFromVoiceoverAssetPlan({
    videoDraftId,
    audioUuid,
    assetKind,
    title,
    templateId,
    aspectRatio,
    blueprintId,
    creationToolName,
}: {
    videoDraftId: string;
    audioUuid: string;
    assetKind: 'audio' | 'video';
    title: string;
    templateId: string;
    aspectRatio: string;
    blueprintId?: number | null;
    creationToolName: VideoDraftCreationTool;
}) {
    return async (
        dispatch: AppDispatch,
        getState: GetRootState,
    ): Promise<{ success: true } | { success: false; errorMessage?: string }> => {
        // Attached-from-uploads assets (media-library picks / dashboard hand-offs) live only in
        // the registries, not the uploads slice, so fall back to resolving them from there.
        const uploadedAsset =
            getUploadedAssetByUuid(getState(), audioUuid) ??
            (await resolveUploadedAssetFromRegistries(audioUuid));
        if (!uploadedAsset) {
            return {
                success: false,
                errorMessage: "We couldn't find your uploaded file. Please try uploading it again.",
            };
        }

        // Sketch-only path: both branches below build the video through the legacy
        // recommendation pipeline. Toolbox doesn't use Sketch, so reaching here as a
        // toolbox user means something routed into Sketch that shouldn't have.
        reportLegacyVideoRecommendationCallForChatUser(
            'createAndPopulateVideoDraftFromVoiceoverAssetPlan',
            { assetKind, creationToolName },
        );

        const isAudioRequest = assetKind === 'audio';
        if (isAudioRequest) {
            if (!isImmutableAudioAsset(uploadedAsset)) {
                return {
                    success: false,
                    errorMessage:
                        "The uploaded file isn't an audio file. Please upload an audio file to continue.",
                };
            }
        } else {
            if (
                isImmutableAudioAsset(uploadedAsset) ||
                !immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(uploadedAsset)
            ) {
                return {
                    success: false,
                    errorMessage:
                        "The uploaded file isn't a supported video file. Please upload a video to continue.",
                };
            }
        }

        const luminary = new Luminary({
            ...DEFAULT_LUMINARY_CONFIG,
            resolutionValue: getVideoDraftLuminaryResolution(),
        });

        dispatch(
            addVideoDraft({
                id: videoDraftId,
                luminary,
                name: title,
                estimatedLoadingDurationSeconds: 120,
                creationToolName,
                videoPlan: {
                    id: videoDraftId,
                    title,
                    content_prompt: null,
                    video_prompt: null,
                    script: null,
                    aspect_ratio: aspectRatio as AspectRatio,
                    template_id: templateId,
                    narrative_thread: NarrativeThreadEnum.VOICEOVER,
                    blueprint_id: blueprintId ?? null,
                    duration_target_seconds: null,
                    ai_voice: null,
                    ai_avatar: null,
                },
            }),
        );

        try {
            const { setCapabilities } = makeOperationsCallable(
                { setCapabilitiesOperation },
                luminary,
                dispatch,
                getState,
            );

            setCapabilities();

            await dispatch(loadTemplateAndSceneDesigns(templateId));
            const templateBundle = getTemplateBundle(getState(), templateId);
            if (!templateBundle) {
                throw new Error(`Could not fetch template with ID ${templateId}.`);
            }
            setLuminaryTemplate(luminary, templateBundle);

            luminary.aspectRatio = aspectRatio as AspectRatio;

            if (isImmutableAudioAsset(uploadedAsset)) {
                const { addSceneGroupFromVoiceoverAsset } = makeOperationsCallable(
                    { addSceneGroupFromVoiceoverAssetOperation },
                    luminary,
                    dispatch,
                    getState,
                );

                const audioAsset = uploadedAsset.set('has_voiceover', true);

                const result = await addSceneGroupFromVoiceoverAsset({
                    targetIndex: 0,
                    audioAssetData: audioAsset,
                    deleteOtherScenes: false,
                    analyticsMetadata: { method: 'Sketch Voiceover Upload' },
                    selectedBlueprintId: blueprintId ?? null,
                    isGlobalVideo: false,
                    systemTriggered: true,
                    parentActionGroup: 'create-video-from-voiceover',
                });

                if (!result) {
                    throw new Error(
                        'Failed to create voiceover video. The voiceover processing may have failed.',
                    );
                }
            } else if (
                immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(uploadedAsset)
            ) {
                const { createNewVoicedrivenSceneGroup, applyTalkingHeadMedia } =
                    makeOperationsCallable(
                        {
                            createNewVoicedrivenSceneGroupOperation,
                            applyTalkingHeadMediaOperation,
                        },
                        luminary,
                        dispatch,
                        getState,
                    );

                const sceneGroupResult = createNewVoicedrivenSceneGroup(
                    VideoData.CATEGORY_TALKING_HEAD,
                    0,
                    { deleteOtherScenes: false, setPlaceholderAsset: false },
                );

                const videoAsset = uploadedAsset.setIn(['video', 'has_voiceover'], true);

                await applyTalkingHeadMedia({
                    sceneGroupId: sceneGroupResult.sceneGroupId,
                    media: videoAsset,
                    language_code: null,
                    forceCaptionGeneration: false,
                    enhanceAudio: true,
                    recommendOverlays: true,
                    blueprintId: blueprintId ?? null,
                    trimRequired: false,
                    systemTriggered: true,
                });
            }

            if (luminary.scenes.length === 0) {
                throw new Error(
                    'Video creation completed but no scenes were created. The voiceover processing may have failed.',
                );
            }

            await dispatch(generateThumbnailFromVideoDraftOperation(videoDraftId));
            dispatch(setVideoDraftGenerating({ id: videoDraftId, isGenerating: false }));
            return { success: true };
        } catch (error) {
            ErrorReporter.capture(error);
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to generate video';
            dispatch(setVideoDraftError({ id: videoDraftId, error_message: errorMessage }));
            dispatch(setVideoDraftGenerating({ id: videoDraftId, isGenerating: false }));
            return { success: false, errorMessage };
        }
    };
}

const SKETCH_LEAVE_MESSAGE =
    'Are you sure you want to quit? Any changes since your last save will not be saved.';

let sketchBeforeUnloadHandler: ((event: BeforeUnloadEvent) => void) | null = null;

export function updateSketchLeaveWarning(shouldShow: boolean) {
    if (sketchBeforeUnloadHandler) {
        window.removeEventListener('beforeunload', sketchBeforeUnloadHandler);
        sketchBeforeUnloadHandler = null;
    }

    if (shouldShow) {
        sketchBeforeUnloadHandler = (event: BeforeUnloadEvent) => {
            if (SERVER.DEBUG) {
                Toast.info('Disabling window.onbeforeunload warning in DEBUG mode', 'FYI!');
                return null;
            }
            event.preventDefault();
            return SKETCH_LEAVE_MESSAGE;
        };
        window.addEventListener('beforeunload', sketchBeforeUnloadHandler);
    }
}
